import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  CheckCircle,
  XCircle,
  Clock,
  ArrowRight,
  RefreshCw,
  Home,
  Receipt,
  Sparkles
} from "lucide-react";
import { Button } from "../components/ui/button";
import { MetaPixel } from "../utils/metaPixel";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { fetchUser } = useAuth();
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);

  const sessionId = searchParams.get("session_id");
  const linkId = searchParams.get("link_id");
  const paymentType = searchParams.get("type"); // "simulacro" for human simulation
  const isUpgrade = searchParams.get("upgrade") === "true";
  const upgradePlan = searchParams.get("plan");

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus("error");
    }
  }, [sessionId]);

  // Redirect to schedule page if it's a simulacro payment
  useEffect(() => {
    if (status === "success" && paymentType === "simulacro") {
      // Auto-redirect to scheduling page after 3 seconds
      const timer = setTimeout(() => {
        navigate("/schedule-simulation");
      }, 3000);
      return () => clearTimeout(timer);
    }
    
    // Track purchase with Meta Pixel for all successful payments
    if (status === "success" && paymentData) {
      // Refresh user data on successful upgrade
      if (isUpgrade) {
        fetchUser();
      }
      
      // Track purchase event
      MetaPixel.trackPurchase({
        plan_name: isUpgrade 
          ? (upgradePlan === "vip" ? "VIP Enterprise" : "Premium")
          : (paymentData.product_name || "Unknown"),
        plan_id: isUpgrade 
          ? `plan_${upgradePlan}` 
          : (paymentData.product_id || "unknown"),
        price: paymentData.amount || 0,
        content_name: isUpgrade ? "Plan Upgrade" : "Purchase",
        transaction_id: paymentData.transaction_id
      });
    }
  }, [status, paymentType, isUpgrade, paymentData, navigate, fetchUser, upgradePlan]);

  const checkPaymentStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/payments/status/${sessionId}`, { headers });
      setPaymentData(res.data);
      
      if (res.data.payment_status === "paid" || res.data.status === "complete") {
        setStatus("success");
      } else if (res.data.payment_status === "pending") {
        setStatus("pending");
        // Poll again after 3 seconds
        setTimeout(checkPaymentStatus, 3000);
      } else {
        setStatus("error");
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      setStatus("error");
    }
  };

  const getStatusContent = () => {
    switch (status) {
      case "loading":
        return {
          icon: <RefreshCw className="w-12 h-12 text-emerald-600 animate-spin" />,
          title: "Verificando pago...",
          description: "Estamos confirmando tu transacción con Stripe.",
          bg: "bg-emerald-100"
        };
      case "success":
        // Special message for simulacro
        if (paymentType === "simulacro") {
          return {
            icon: <CheckCircle className="w-12 h-12 text-emerald-600" />,
            title: "¡Simulacro Comprado!",
            description: "Serás redirigido para agendar tu sesión de práctica...",
            bg: "bg-emerald-100"
          };
        }
        // Special message for plan upgrade
        if (isUpgrade) {
          const planName = upgradePlan === "vip" ? "VIP Enterprise" : "Premium";
          return {
            icon: <Sparkles className="w-12 h-12 text-amber-500" />,
            title: `¡Bienvenido a ${planName}!`,
            description: "Tu plan ha sido actualizado. Ya tienes acceso a todas las nuevas funciones.",
            bg: "bg-gradient-to-br from-amber-100 to-orange-100"
          };
        }
        return {
          icon: <CheckCircle className="w-12 h-12 text-emerald-600" />,
          title: "¡Pago Exitoso!",
          description: "Tu transacción ha sido procesada correctamente.",
          bg: "bg-emerald-100"
        };
      case "pending":
        return {
          icon: <Clock className="w-12 h-12 text-amber-600" />,
          title: "Pago en Proceso",
          description: "Tu pago está siendo procesado. Por favor espera un momento.",
          bg: "bg-amber-100"
        };
      case "error":
        return {
          icon: <XCircle className="w-12 h-12 text-red-600" />,
          title: "Error en el Pago",
          description: "No pudimos confirmar tu pago. Por favor intenta nuevamente.",
          bg: "bg-red-100"
        };
      default:
        return {
          icon: <Clock className="w-12 h-12 text-navy-600" />,
          title: "Estado Desconocido",
          description: "No pudimos determinar el estado de tu pago.",
          bg: "bg-navy-100"
        };
    }
  };

  const content = getStatusContent();

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 to-navy-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center"
      >
        {/* Icon */}
        <div className={`w-24 h-24 ${content.bg} rounded-full flex items-center justify-center mx-auto mb-6`}>
          {content.icon}
        </div>

        {/* Title & Description */}
        <h1 className="font-heading text-2xl font-bold text-navy-900 mb-2">
          {content.title}
        </h1>
        <p className="text-navy-600 mb-6">{content.description}</p>

        {/* Payment Details (if success) */}
        {status === "success" && paymentData && (
          <div className="bg-navy-50 rounded-xl p-5 mb-6 text-left">
            <h3 className="font-semibold text-navy-900 mb-3 flex items-center gap-2">
              <Receipt className="w-4 h-4" />
              Detalles del Pago
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-navy-500">Producto:</span>
                <span className="font-medium text-navy-900">{paymentData.product_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-navy-500">Monto:</span>
                <span className="font-bold text-emerald-600">${paymentData.amount?.toFixed(2)} USD</span>
              </div>
              <div className="flex justify-between">
                <span className="text-navy-500">Estado:</span>
                <span className="text-emerald-600 font-medium">Completado</span>
              </div>
              {paymentData.transaction_id && (
                <div className="flex justify-between">
                  <span className="text-navy-500">ID:</span>
                  <span className="font-mono text-xs text-navy-600">{paymentData.transaction_id.slice(0, 8)}...</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          {status === "success" && (
            <Button
              onClick={() => navigate("/dashboard")}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              Ir al Dashboard
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
          
          {status === "error" && (
            <Button
              onClick={() => linkId ? navigate(`/pagar/${linkId}`) : navigate("/vendedor/pagos")}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              Intentar Nuevamente
              <RefreshCw className="w-4 h-4 ml-2" />
            </Button>
          )}
          
          <Button
            variant="outline"
            onClick={() => navigate("/")}
            className="w-full"
          >
            <Home className="w-4 h-4 mr-2" />
            Volver al Inicio
          </Button>
        </div>

        {/* Footer */}
        <p className="text-xs text-navy-400 mt-6">
          Si tienes algún problema con tu pago, contacta a tu asesor de Divídelo.
        </p>
      </motion.div>
    </div>
  );
};

export default PaymentSuccessPage;
